import { type ReactNode } from 'react';

import { type PermissionCode } from './permission';

export interface DashboardItem {
  label: string;
  icon: ReactNode;
  link: string;
  items?: DashboardItem[];
  component?: ReactNode;
  disabled?: boolean;
  /**
   * Hides the entry unless the viewer holds one of these on the current scope. Left
   * out, the entry is always visible — for pages no permission governs.
   */
  permissions?: PermissionCode[];
}

/**
 * The codes that open an entry: its own, plus its children's, since a section is
 * visible when anything inside it is. Undefined means nothing gates it — a child
 * with no permissions makes the whole section public, and an empty `oneOf` would
 * deny everyone rather than allow them.
 */
export const gateFor = ({
  permissions,
  items = [],
}: DashboardItem): PermissionCode[] | undefined => {
  if (items.some((child) => !child.permissions)) return undefined;

  // Deduped: a section and its children usually share a code, and the union is what
  // the Sandbox switch reports as the page's gate.
  const union = [
    ...new Set([
      ...(permissions ?? []),
      ...items.flatMap((child) => child.permissions ?? []),
    ]),
  ];

  return union.length > 0 ? union : undefined;
};

/**
 * Every gated entry of a menu tree as a `{ to, oneOf }` pair, `to` being the path
 * under the tree's root. Depth-first, so the order is the order the sidebar paints —
 * which is what `firstAllowed` walks.
 */
export const flattenGates = (
  items: DashboardItem[],
  prefix = '',
): { to: string; oneOf: PermissionCode[] }[] =>
  items.flatMap((item) => {
    const to = prefix ? `${prefix}/${item.link}` : item.link;
    const oneOf = gateFor(item);

    return [
      ...(oneOf ? [{ to, oneOf }] : []),
      ...flattenGates(item.items ?? [], to),
    ];
  });
